"use client";

import { useState, useEffect, useRef } from "react";
import Link from "next/link";
import { Logo } from "@/components/ui/Logo";
import {
  MapPinIcon,
  ShieldCheckIcon,
  Bars3Icon,
  ArrowDownTrayIcon,
} from "@heroicons/react/24/outline";
import { useAuth } from "@/contexts/AuthContext";
import { usePathname, useRouter } from "next/navigation";
import { usePWAInstall } from "@/hooks/usePWAInstall";
import { useReferralMe } from "@/lib/hooks/useReferralMe";
import NotificationBell from "@/components/notifications/NotificationBell";
import NotificationDropdownPanel from "@/components/notifications/NotificationDropdownPanel";
import NotificationSetupPassiveChip from "@/components/notifications/NotificationSetupPassiveChip";
import HeaderLocationDisplay from "@/components/layout/HeaderLocationDisplay";
import AppearanceSettings from "@/components/profile/AppearanceSettings";
import { cn } from "@/lib/utils";

interface MobileHeaderProps {
  compact?: boolean;
}

const MENU_PANEL_ID = "mobile-header-menu-dropdown";
const NOTIFICATIONS_PANEL_ID = "mobile-notification-dropdown";

const menuItemClass =
  "block w-full rounded-lg px-3 py-2.5 text-left text-sm font-medium text-foreground hover:bg-secondary";

const MobileHeader = ({ compact = false }: MobileHeaderProps) => {
  const { user, isAuthenticated, isLoading: isAuthLoading, logout } = useAuth();
  const { isPartner: referralMenuPartner } = useReferralMe(
    isAuthenticated && !isAuthLoading,
  );
  const showReferralHubLink =
    referralMenuPartner || user?.partner_type === "market_partner";
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const notificationWrapRef = useRef<HTMLDivElement>(null);
  const menuWrapRef = useRef<HTMLDivElement>(null);
  const router = useRouter();
  const pathname = usePathname();
  const currentPath = pathname ?? "/";
  const { canInstall, isInstalled, install } = usePWAInstall();

  const closeMenu = () => setIsMenuOpen(false);

  const handleLogout = async () => {
    setIsNotificationsOpen(false);
    await logout();
    closeMenu();
  };

  const handleSectionNavigation = (sectionId: string) => {
    closeMenu();
    setIsNotificationsOpen(false);

    if (currentPath !== "/") {
      router.push(`/#${sectionId}`);
      return;
    }

    let section = document.getElementById(sectionId);

    if (!section) {
      section = document.getElementById(`#${sectionId}`);
    }

    if (section) {
      section.scrollIntoView({ behavior: "smooth" });
    } else {
      router.push(`/#${sectionId}`);
    }
  };

  useEffect(() => {
    const handleHashNavigation = () => {
      const hash = window.location.hash.replace("#", "");
      if (hash) {
        let section = document.getElementById(hash);

        if (!section) {
          section = document.getElementById(`#${hash}`);
        }

        if (section) {
          setTimeout(() => {
            section?.scrollIntoView({ behavior: "smooth" });
          }, 300);
        }
      }
    };

    handleHashNavigation();
    window.addEventListener("hashchange", handleHashNavigation);

    return () => {
      window.removeEventListener("hashchange", handleHashNavigation);
    };
  }, []);

  useEffect(() => {
    if (!isMenuOpen && !isNotificationsOpen) return;

    const handlePointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!notificationWrapRef.current?.contains(target)) {
        setIsNotificationsOpen(false);
      }
      if (!menuWrapRef.current?.contains(target)) {
        closeMenu();
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsNotificationsOpen(false);
        closeMenu();
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isMenuOpen, isNotificationsOpen]);

  return (
    <div className={`lg:hidden ${compact ? "mt-14" : "mt-16"}`}>
      {isMenuOpen ? (
        <div
          className="fixed inset-0 z-[9999] bg-foreground/20"
          aria-hidden="true"
          onClick={closeMenu}
        />
      ) : null}

      <div
        className={cn(
          "fixed top-0 left-0 right-0 m-4 w-auto max-w-screen sio-glass rounded-sio-lg transition-all duration-300",
          isMenuOpen ? "z-[10020]" : "z-[10000]",
        )}
        style={{ boxShadow: "0px 0px 10px 0px rgba(0, 0, 0, 0.1)" }}
      >
        <div className={`px-4 ${compact ? "py-2.5" : "py-3"}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <Link href="/" className="ml-2 flex items-center">
                <Logo
                  width={32}
                  height={32}
                  className="mr-2"
                  style={{ width: "32px", height: "32px" }}
                />
                <span className="hidden whitespace-nowrap text-lg font-bold text-primary sm:inline">
                  Sell It Off
                </span>
              </Link>
            </div>

            <div className="flex items-center space-x-2">
              <div className="flex items-center text-xs text-text-light">
                <MapPinIcon className="mr-1 h-3 w-3 text-primary" />
                <HeaderLocationDisplay
                  className="truncate text-xs"
                  loadingClassName="text-text-light text-xs"
                />
              </div>

              {isAuthenticated ? <NotificationSetupPassiveChip /> : null}

              {isAuthenticated && (
                <div className="relative" ref={notificationWrapRef}>
                  <NotificationBell
                    mode="dropdown"
                    ariaExpanded={isNotificationsOpen}
                    ariaControls={NOTIFICATIONS_PANEL_ID}
                    onClick={() => {
                      closeMenu();
                      setIsNotificationsOpen((currentOpen) => !currentOpen);
                    }}
                  />
                  {isNotificationsOpen ? (
                    <NotificationDropdownPanel
                      panelId={NOTIFICATIONS_PANEL_ID}
                      userId={user?.id}
                      onClose={() => setIsNotificationsOpen(false)}
                    />
                  ) : null}
                </div>
              )}
              {user?.isAdmin && (
                <Link
                  href="/admin"
                  className="rounded-md p-2 hover:bg-tertiary"
                  aria-label="Admin dashboard"
                >
                  <ShieldCheckIcon className="h-5 w-5 text-primary" />
                </Link>
              )}

              <div className="relative" ref={menuWrapRef}>
                <button
                  type="button"
                  className="-ml-3 p-2 text-text-light hover:text-foreground"
                  aria-expanded={isMenuOpen}
                  aria-controls={MENU_PANEL_ID}
                  onClick={() => {
                    setIsNotificationsOpen(false);
                    setIsMenuOpen((open) => !open);
                  }}
                >
                  <span className="sr-only">
                    {isMenuOpen ? "Close menu" : "Open menu"}
                  </span>
                  <Bars3Icon className="h-6 w-6" aria-hidden="true" />
                </button>

                {isMenuOpen ? (
                  <div
                    id={MENU_PANEL_ID}
                    role="navigation"
                    aria-label="Menu"
                    className={cn(
                      "absolute right-0 top-full z-[1] mt-2 flex w-[min(20rem,calc(100vw-2rem))] min-w-[14rem] flex-col overflow-hidden rounded-xl border border-border bg-surface shadow-xl",
                      "max-h-[calc(100dvh-5.5rem-env(safe-area-inset-bottom)-5rem)]",
                    )}
                  >
                    <nav className="overflow-y-auto px-2 py-2">
                      {isAuthenticated ? (
                        <>
                          <Link
                            href="/request-item"
                            className={menuItemClass}
                            onClick={closeMenu}
                          >
                            Request an item
                          </Link>
                          <Link
                            href="/my-requests"
                            className={menuItemClass}
                            onClick={closeMenu}
                          >
                            My Requests
                          </Link>
                          <Link
                            href="/my-purchases"
                            className={menuItemClass}
                            onClick={closeMenu}
                          >
                            My Purchases
                          </Link>
                          {showReferralHubLink ? (
                            <Link
                              href="/refer"
                              className={menuItemClass}
                              onClick={closeMenu}
                            >
                              Referral Hub
                            </Link>
                          ) : null}
                        </>
                      ) : null}
                      <button
                        type="button"
                        className={menuItemClass}
                        onClick={() => handleSectionNavigation("how-it-works")}
                      >
                        How It Works
                      </button>
                      <button
                        type="button"
                        className={menuItemClass}
                        onClick={() => handleSectionNavigation("testimonials")}
                      >
                        Testimonials
                      </button>
                      <Link
                        href="/#contact"
                        className={menuItemClass}
                        onClick={() => handleSectionNavigation("contact")}
                      >
                        Contact Us
                      </Link>
                      <button
                        type="button"
                        className={menuItemClass}
                        onClick={() => handleSectionNavigation("faqs")}
                      >
                        FAQs
                      </button>
                      {canInstall && !isInstalled ? (
                        <button
                          type="button"
                          className={cn(menuItemClass, "flex items-center")}
                          onClick={async () => {
                            try {
                              await install();
                            } catch (_error) {
                              // Silently handle errors
                            } finally {
                              closeMenu();
                            }
                          }}
                        >
                          <ArrowDownTrayIcon className="mr-2 h-5 w-5" />
                          Install App
                        </button>
                      ) : null}
                    </nav>

                    <div className="shrink-0 border-t border-border">
                      <AppearanceSettings variant="sidebar" className="px-2" />

                      {isAuthenticated ? (
                        <div className="px-2 pb-3">
                          <button
                            type="button"
                            onClick={handleLogout}
                            className="w-full rounded-lg px-3 py-2.5 text-sm font-medium text-red-600 hover:bg-red-50"
                          >
                            Log out
                          </button>
                        </div>
                      ) : (
                        <div className="flex flex-col gap-2 px-2 pb-3">
                          <Link
                            href="/login"
                            className="block w-full rounded-lg bg-secondary px-3 py-2 text-center text-sm font-medium text-foreground hover:bg-tertiary"
                            onClick={closeMenu}
                          >
                            Log in
                          </Link>
                          <Link
                            href="/signup"
                            className="block w-full rounded-lg bg-primary-500 px-3 py-2 text-center text-sm font-medium text-white hover:bg-primary-600"
                            onClick={closeMenu}
                          >
                            Sign up
                          </Link>
                        </div>
                      )}
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobileHeader;
